import Fingerprint2 from "fingerprintjs2";
import FingerprintJS from "@fingerprintjs/fingerprintjs-pro";

import { getSafe } from "@jslib/shared/utils/getSafe";
import { logger } from "@jslib/shared/utils/logger";

import { getOptions } from "../../options/options";
import { type ProgramTheme } from "../types/types";

type FingerprintProperties = {
  clientId: string;
  workerEndpoint: string;
  scriptUrl: string;
};

/**
 * @description The user's device fingerprint
 */
let fingerprint: string;

/**
 * @description Get the digital fingerprint hash for a user, based on various digital signals.
 *              Recommended: call this at least 500ms after page ready
 */
export const getFingerprint = async (): Promise<string> => {
  if (!getOptions().useFingerprinting) {
    return Promise.resolve(undefined);
  }

  if (fingerprint) {
    return Promise.resolve(fingerprint);
  }

  return Fingerprint2.getPromise({}).then((components) => {
    const values = components.map((component) => component.value);
    const murmur = Fingerprint2.x64hash128(values.join(""), 31);
    fingerprint = murmur;
    return fingerprint;
  });
};

/**
 * @description Gets Fingerprint config from window properties passed in from view-main template.
 *              This avoids env specific logic in jslib.
 */
const getFingerprintProperties = (): FingerprintProperties => {
  const { clientId, workerEndpoint, scriptUrl } = window.FP || {};
  if (
    !clientId ||
    !scriptUrl ||
    !scriptUrl.match(/^https:\/\/worker(-preview)?\.sheerid\.com\//) ||
    !workerEndpoint ||
    !workerEndpoint.match(/^https:\/\/worker(-preview)?\.sheerid\.com\//)
  ) {
    throw new Error("Fingerprint environment not set properly");
  }

  return { clientId, workerEndpoint, scriptUrl };
};

let fpRequestId: string | null = null;

export const getFingerprintRequestId = (): string | null => fpRequestId;

/**
 * @description Loads the Fingerprint SDK and collects device signals. Returns the requestId
 *              so it can be passed as metadata on submit, and saves it for retrieval via
 *              getFingerprintRequestId().
 */
export const collectFingerprintDeviceData = async (
  programTheme: ProgramTheme,
  verificationId: string,
): Promise<string | undefined> => {
  if (!getSafe(() => programTheme.fingerprintEnabled, false)) {
    return undefined;
  }

  try {
    const { clientId, workerEndpoint, scriptUrl } = getFingerprintProperties();

    const fp = await FingerprintJS.load({
      apiKey: clientId,
      endpoint: [workerEndpoint, FingerprintJS.defaultEndpoint],
      scriptUrlPattern: [scriptUrl, FingerprintJS.defaultScriptUrlPattern],
    });

    const result = await fp.get({ linkedId: verificationId });
    fpRequestId = result.requestId;
    return result.requestId;
  } catch (error) {
    logger.error(error, "Failed to collect Fingerprint device data");
    return undefined;
  }
};
